import { BundleItem, ProductImage, RootData } from 'src/types/graphql/product';
import { PLAN_OPTIONS, PRODUCT_TYPES } from 'src/types/subscription';
import moment from 'moment';
import recommendPrinter from '../recommendPrinter';
import { RecommendedPrinterUpgradeRules } from 'src/types/featureFlags';
import recommendPrinterPlans from '../recommendPrinterPlans';
import filterCancellationFeeList from '../cancellationFeeList';

// Type definitions for subscription options data
interface Outcome {
  type: string;
  value?: {
    productType?: string;
    price?: {
      cost?: number;
    };
  };
}

interface EditEntity {
  product?: {
    value?: {
      productSku?: string;
      pagesPerMonth?: number;
      price?: {
        cost?: number;
      };
      maxRolloverPages?: number;
    };
  };
  outcomes?: Outcome[];
}

interface OptionsData {
  entities?: Array<{
    edit?: EditEntity[];
  }>;
}

export interface InkPlan {
  inkSKU: string;
  pagesPerMonth: number;
  leasePrice: number;
  paperPrice: number;
  planPrice: number;
  totalPrice: number;
  recommendedPlan: boolean;
  commitmentPeriod: number;
  maxRolloverPages: number | null;
  inkUID?: string;
  leaseUID?: string;
  paperUID?: string;
  carepackUID?: string;
}

export interface PrinterOfferPlan {
  fullspecification: string;
  specificationList: string;
  thumbnailImage: string;
  shortDescription: string;
  shortConfigName: string;
  mediaGallery: ProductImage[];
  stockStatus: string;
  supplyTypeLabel?: string;
  printerUID: string;
}
export interface LeasePlan {
  price: number;
  leaseUID: string;
}

export interface CarePacK {
  price: number;
  carepackUID: string;
}

export interface CancellationFeeItem {
  apply: string;
  term: string;
  fees: string;
  sku: string;
}

export interface PrinterItem {
  printerName: string;
  recommendedPrinter: boolean;
  currentPrinter: boolean;
  printerUID: string;
  productSKU: string;
  modelPurpose: string;
  stockStatus: string;
  isPaperActive: boolean;
  mediaGallery: ProductImage[];
  inkPlan: InkPlan[];
  fullspecification: string;
  specificationList: string;
  thumbnailImage: string;
  shortDescription: {
    html: string;
  };
  cancellationFeeList: CancellationFeeItem[];
  cissPrinter?: boolean;
}

export interface PrinterSpecs {
  adfCapacity: number;
  autoDocFeeder: boolean;
  builtInFax: boolean;
  dimensions: {
    width: number;
    depth: number;
    height: number;
    unit: string;
  };
  ethernetNetworking: boolean;
  inkType: string;
  lcd: boolean;
  memoryCard: boolean;
  paperInput: number;
  photoTray: boolean;
  printCopyScan: boolean;
  printSpeed: number;
  wirelessCapability: boolean;
  aiEnhanced: boolean;
}

export function buildPrinterList(
  data: RootData,
  skuList: string[],
  activePaperSubscription: boolean,
  optionsData: OptionsData,
  inkProductSku: string,
  recommendationRules: RecommendedPrinterUpgradeRules[]
): { currentPrinterList: PrinterItem[]; nonCurrentPrinters: PrinterItem[] } {
  const products = data?.products?.items ?? [];
  const optionDetails = optionsData?.entities?.[0]?.edit;
  const filteredEntities = optionDetails?.filter(
    (entity: EditEntity) => entity.product?.value?.productSku === inkProductSku
  );

  const currentInkPlan = getCurrentPrinterInkPlan(
    filteredEntities,
    activePaperSubscription
  ) as InkPlan;
  const currentPagesPerMonth = currentInkPlan?.pagesPerMonth;

  const printerList: PrinterItem[] = skuList.reduce(
    (acc: PrinterItem[], sku, index) => {
      const foundProduct = products.find((product) => product.sku === sku);
      if (foundProduct) {
        const currentPrinter = index === 0; //Only first item from skuList is current printer
        let printerOfferPlans: PrinterOfferPlan[] = [];
        let printerName = '';

        const offerPlansResult = getCoreOfferPlans(foundProduct?.items);
        printerOfferPlans = offerPlansResult?.printerOfferPlans || [];
        printerName =
          printerOfferPlans.length > 0
            ? printerOfferPlans[0].shortConfigName
            : '';

        acc.push({
          printerName,
          recommendedPrinter: false,
          printerUID: printerOfferPlans?.[0]?.printerUID || '',
          cissPrinter: printerOfferPlans?.[0]?.supplyTypeLabel === 'inkTank',
          currentPrinter,
          modelPurpose: foundProduct.hp_model_purpose,
          productSKU: foundProduct.sku,
          stockStatus:
            !currentPrinter && printerOfferPlans.length > 0
              ? printerOfferPlans[0].stockStatus
              : '',
          isPaperActive: activePaperSubscription,
          mediaGallery:
            !currentPrinter && printerOfferPlans.length > 0
              ? printerOfferPlans[0].mediaGallery
              : [],
          inkPlan: !currentPrinter
            ? getInkPlans(foundProduct?.items, activePaperSubscription).inkPlans
            : [currentInkPlan],
          fullspecification:
            !currentPrinter && printerOfferPlans.length > 0
              ? printerOfferPlans[0].fullspecification
              : '',
          specificationList:
            !currentPrinter && printerOfferPlans.length > 0
              ? printerOfferPlans[0].specificationList
              : '',
          thumbnailImage:
            !currentPrinter && printerOfferPlans.length > 0
              ? printerOfferPlans[0].thumbnailImage
              : '',
          shortDescription: {
            html:
              !currentPrinter && printerOfferPlans.length > 0
                ? printerOfferPlans[0].shortDescription || ''
                : ''
          },
          cancellationFeeList: filterCancellationFeeList(
            foundProduct?.hp_cancellation_json
          )
        });
      }
      return acc;
    },
    []
  );

  // ✅ Split into two lists
  const currentPrinterList = printerList.filter((p) => p.currentPrinter);
  const nonCurrentPrinters = printerList.filter((p) => !p.currentPrinter);

  // get recommended printer
  const recommendedPrinterName = recommendPrinter(
    currentPrinterList[0]?.printerName,
    currentPagesPerMonth,
    recommendationRules
  );

  // add recommended plans for each printer
  const recommendedPrinterList = recommendPrinterPlans(
    nonCurrentPrinters,
    currentPagesPerMonth
  ).map((printer) => ({
    ...printer,
    recommendedPrinter:
      printer.printerName.toLowerCase() === recommendedPrinterName.toLowerCase()
  }));
  return { currentPrinterList, nonCurrentPrinters: recommendedPrinterList };
}

function getInkPlans(
  bundleItems: BundleItem[],
  activePaperSubscription: boolean
): { inkPlans: InkPlan[] } {
  // Extract lease plans
  const leasePlans: LeasePlan[] = bundleItems.flatMap((item) =>
    item.options
      .filter(
        (option) =>
          option.product.hp_product_type_label ===
          PRODUCT_TYPES.LEASE_RECEIVABLE
      )
      .map((option) => ({
        price: option.product.price?.regularPrice?.amount?.value ?? 0,
        leaseUID: option.uid
      }))
  );

  const carepackDetails: CarePacK[] = bundleItems.flatMap((item) =>
    item.options
      .filter(
        (option) =>
          option.product.hp_product_type_label === PRODUCT_TYPES.CAREPACK
      )
      .map((option) => ({
        price: option.product.price?.regularPrice?.amount?.value ?? 0,
        carepackUID: option.uid
      }))
  );

  // Extract paper plans mapped by pagesPerMonth
  const paperPlansMap: Record<number, { price: number; paperUID: string }> =
    bundleItems
      .flatMap((item) =>
        item.options
          .filter(
            (option) =>
              activePaperSubscription &&
              option.product.hp_product_type_label ===
                PRODUCT_TYPES.INSTANT_PAPER
          )
          .map((option) => {
            return {
              pages: option.product.hp_pages_per_month ?? 0,
              price: option.product.price?.regularPrice?.amount?.value ?? 0,
              paperUID: option.uid
            };
          })
      )
      .reduce(
        (map, { pages, price, paperUID }) => {
          map[pages] = { price, paperUID };
          return map;
        },
        {} as Record<number, { price: number; paperUID: string }>
      );

  // ✅ Instant Ink Plans
  const inkPlans: InkPlan[] = bundleItems.flatMap((item) =>
    item.options
      .filter(
        (option) =>
          option.product.hp_product_type_label === PRODUCT_TYPES.INSTANT_INK &&
          option.hp_plan_option !== PLAN_OPTIONS.UNAVAILABLE
      )
      .map((option, index) => {
        const product = option.product;
        const pages = product.hp_pages_per_month ?? 0;
        const paperPrice = paperPlansMap[pages]?.price ?? 0;
        const months = product.hp_contract_duration ?? 0;
        const duration = moment.duration(months, 'months');
        const upgradeEligibility = duration.years();
        const inkUID = option.uid;

        return {
          inkSKU: product.sku,
          pagesPerMonth: pages,
          commitmentPeriod: upgradeEligibility,
          leasePrice: leasePlans.length > 0 ? leasePlans[0].price : 0,
          leaseUID: leasePlans.length > 0 ? leasePlans[0].leaseUID : '',
          carepackUID:
            carepackDetails.length > 0 ? carepackDetails[0].carepackUID : '',
          paperPrice,
          planPrice: product.price?.regularPrice?.amount?.value ?? 0,
          totalPrice:
            (leasePlans.length > 0 ? leasePlans[0].price : 0) +
            paperPrice +
            (product.price?.regularPrice?.amount?.value ?? 0),
          recommendedPlan: index === 0, // First plan is recommended
          maxRolloverPages: product.hp_max_rollover_pages || 0,
          supplyTypeLabel: product.hp_supply_type_label,
          inkUID,
          paperUID: paperPlansMap[pages]?.paperUID
        };
      })
  );

  return { inkPlans };
}

function getCoreOfferPlans(bundleItems: BundleItem[]): {
  printerOfferPlans: PrinterOfferPlan[];
} {
  const printerOfferPlans: PrinterOfferPlan[] = bundleItems.flatMap((item) => {
    const printerOptions = item.options.filter(
      (option) => option.product.hp_product_type_label === PRODUCT_TYPES.PRINTER
    );

    if (printerOptions.length > 0) {
      const product = printerOptions[0].product; // ✅ Take only the first option
      return [
        {
          printerSKU: product.sku,
          fullspecification: product.hp_product_full_specification ?? '',
          printerUID: printerOptions[0]?.uid,
          shortConfigName: product.hp_short_config_name ?? '',
          specificationList: product.hp_specifications_json,
          thumbnailImage: product.thumbnail.url,
          shortDescription: product.short_description?.html ?? '',
          mediaGallery: product.media_gallery ?? [],
          stockStatus: product.stock_status,
          supplyTypeLabel: product.hp_supply_type_label
        }
      ];
    }

    return []; // If no printer option exists
  });

  return { printerOfferPlans };
}

function getCurrentPrinterInkPlan(
  filteredEntities: EditEntity[] | undefined,
  activePaperSubscription: boolean
): InkPlan | null {
  const pagesPerMonth =
    filteredEntities?.[0]?.product?.value?.pagesPerMonth || 0;

  const leasePrice =
    filteredEntities?.[0]?.outcomes &&
    filteredEntities?.[0]?.outcomes.length > 0
      ? filteredEntities?.[0]?.outcomes?.filter(
          (outcome: Outcome) =>
            outcome.type === PRODUCT_TYPES.LEASE_PRICE_TYPE &&
            outcome.value?.productType === PRODUCT_TYPES.LEASE_RECEIVABLE
        )?.[0]?.value?.price?.cost || 0
      : 0;

  const paperPrice =
    activePaperSubscription &&
    filteredEntities?.[0]?.outcomes &&
    filteredEntities?.[0]?.outcomes.length > 0
      ? filteredEntities?.[0]?.outcomes?.filter(
          (outcome: Outcome) =>
            outcome.type === PRODUCT_TYPES.LEASE_PRICE_TYPE &&
            outcome.value?.productType === PRODUCT_TYPES.INSTANT_PAPER
        )?.[0]?.value?.price?.cost || 0
      : 0;

  const planPrice = filteredEntities?.[0]?.product?.value?.price?.cost || 0;

  const totalPrice = planPrice + leasePrice + paperPrice;

  return {
    commitmentPeriod: 0,
    inkSKU: filteredEntities?.[0]?.product?.value?.productSku || '',
    recommendedPlan: false,
    pagesPerMonth,
    leasePrice,
    paperPrice,
    planPrice,
    totalPrice,
    maxRolloverPages:
      filteredEntities?.[0]?.product?.value?.maxRolloverPages || 0,
    paperUID: '',
    carepackUID: ''
  };
}
